import { Lightning, AppData } from '@lightningjs/sdk';
import { Channel, Episode, Media, Show } from 'types/api/media';
import { getSearchTileImage, getSeriesName } from 'support/contentUtils';
import { getFontFaceFromStyle } from 'support/textUtils';
import ExpandingDetailTile from 'components/common/tiles/ExpandingDetailTile';
import { translate } from 'support/translate';
import { constants } from 'aliases';
import { BOUNCY_SCROLL, STANDARD_TRANSLATION } from 'support/animations';
import { SelectItemContext } from 'types/events';
import {
  reportBrowsePageEngagement,
  reportItemSelected,
} from 'services/analytics/reportingServicePage';
import { ContentHubLaneItem, LaneItem, Swimlanes } from 'services/cwData';
import SearchCarousel from 'components/common/carousels/SearchCarousel';
import { PageId } from 'types/pageId';
import BezelButtonCarousel from 'components/common/carousels/BezelButtonCarousel';
import BezelContentHubTile from 'components/common/tiles/BezelContentHubTile';
import { TileParentContext } from 'components/common/tiles/Tile';

const NAVBAR_COLLAPSED_WIDTH = constants.sizing.navbar.collapsedWidth;

const LANDING_RESULTS_HEIGHT = 815;

const EXPANDING_ROW_COLLAPSED_HEIGHT = 274.12;
const EXPANDING_ROW_EXPANDED_HEIGHT = 398;
const EXPANDING_ROW_MARGIN_TOP = 12;
const EXPANDING_ROW_MARGIN_BOTTOM = 35;

const INITIAL_Y = 220;

const LINK_ITEMS_CAROUSEL_BAR_DIMENSION = {
  w: 1678,
  h: 148,
  flexItem: {
    marginBottom: 23,
  },
};

const LANDING_RESULTS_WRAPPER_POSITION = {
  x: NAVBAR_COLLAPSED_WIDTH,
  y: INITIAL_Y,
  w: 1758,
};

const LABEL_FONT_SIZE = 32;
const LABEL_HEIGHT = 48;

const LONG_PRESS_THRESHOLD = 200;

export interface LandingResultsTemplateSpec
  extends Lightning.Component.TemplateSpec {
  LandingResultsWrapper: {
    LinkItems: typeof BezelButtonCarousel;
    TrendingResults: {
      List: typeof SearchCarousel;
    };
    RecentResults: {
      List: typeof SearchCarousel;
    };
  };
}

export default class LandingResults
  extends Lightning.Component<LandingResultsTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<LandingResultsTemplateSpec>
{
  private _LandingResultsWrapper = this.getByRef('LandingResultsWrapper')!;

  private _LinkItems = this._LandingResultsWrapper.getByRef('LinkItems')!;

  private _TrendingResults =
    this._LandingResultsWrapper.getByRef('TrendingResults')!;
  private _TrendingResultsList = this._TrendingResults.getByRef('List')!;

  private _RecentResults =
    this._LandingResultsWrapper.getByRef('RecentResults')!;
  private _RecentResultsList = this._RecentResults.getByRef('List')!;

  private _longPressKeyTime: number | null = null;

  static override _template(): Lightning.Component.Template<LandingResultsTemplateSpec> {
    const textProps = {
      h: LABEL_HEIGHT,
      text: {
        fontSize: LABEL_FONT_SIZE,
        fontFace: getFontFaceFromStyle('bold'),
        lineHeight: null,
      },
    };

    const carouselProps = {
      listType: ExpandingDetailTile,
      labelProps: textProps,
      visible: true,
      itemWidth: ExpandingDetailTile.width,
      label: undefined,
      navBarOffset: NAVBAR_COLLAPSED_WIDTH,
      rowListOffset: LABEL_HEIGHT,
      signals: {
        onCarouselHeightChanged: 'onCarouselHeightChanged',
      },
    };

    const rowProps = {
      h: EXPANDING_ROW_COLLAPSED_HEIGHT,
      flexItem: {
        marginTop: EXPANDING_ROW_MARGIN_TOP,
        marginBottom: EXPANDING_ROW_MARGIN_BOTTOM,
      },
    };

    return {
      h: LANDING_RESULTS_HEIGHT,
      LandingResultsWrapper: {
        ...LANDING_RESULTS_WRAPPER_POSITION,
        flex: {
          direction: 'column',
        },
        LinkItems: {
          ...LINK_ITEMS_CAROUSEL_BAR_DIMENSION,
          type: BezelButtonCarousel,
          // @ts-ignore
          listType: BezelContentHubTile,
          labelProps: textProps,
          rowListOffset: LABEL_HEIGHT,
          itemWidth: BezelContentHubTile.width,
          label: undefined,
          laneIndex: 0,
          signals: {
            onCarouselHeightChanged: 'onCarouselHeightChanged',
          },
          navBarOffset: NAVBAR_COLLAPSED_WIDTH,
          passSignals: {
            $onHover: true,
          },
          visible: false,
        },
        TrendingResults: {
          ...rowProps,
          List: {
            ...carouselProps,
            label: translate('search.trendingSearches'),
            type: SearchCarousel,
            laneIndex: 1,
            passSignals: { $onHover: true },
          },
        },
        RecentResults: {
          ...rowProps,
          List: {
            ...carouselProps,
            label: translate('search.recentSearches'),
            type: SearchCarousel,
            laneIndex: 2,
            passSignals: { $onHover: true },
          },
        },
      },
    };
  }

  onCarouselHeightChanged(target: Lightning.Component, isExpanded: boolean) {
    if (target === this._TrendingResultsList) {
      this.expandList(
        this._TrendingResults,
        this._TrendingResultsList,
        isExpanded,
      );
    } else {
      this.expandList(this._RecentResults, this._RecentResultsList, isExpanded);
    }
  }

  get linkItems() {
    return this._LinkItems;
  }

  get trendingResultsList() {
    return this._TrendingResultsList;
  }

  get recentResultsList() {
    return this._RecentResultsList;
  }

  override _unfocus() {
    this.collapseAllLists();
  }

  override _captureLeft(): boolean | void {
    this.handleKeyCapture();
    return false;
  }

  override _captureRight(): boolean | void {
    this.handleKeyCapture();
    return false;
  }

  override _captureLeftRelease(): boolean | void {
    this._longPressKeyTime = null;
    return false;
  }

  override _captureRightRelease(): boolean | void {
    this._longPressKeyTime = null;
    return false;
  }

  setIndices(mainIndex: number, crossIndex: number) {
    if (mainIndex === this._LinkItems.laneIndex) {
      this._LinkItems.index = crossIndex;
      this._setState('LinkItems');
    } else if (mainIndex === this._TrendingResultsList.laneIndex) {
      this._TrendingResultsList.index = crossIndex;
      this._setState('TrendingResults');
    } else if (mainIndex === this._RecentResultsList.laneIndex) {
      this._RecentResultsList.index = crossIndex;
      this._setState('RecentResults');
    }
  }

  resetComponent() {
    this._TrendingResultsList.patch({ laneItems: [] });
  }

  showLandingResults(
    exploreData: Swimlanes | null | undefined,
    trendingData: (Show | Channel | Episode)[],
  ) {
    this.generateLinkItems(exploreData);
    if (this.hasLinkItemsData()) {
      this._LinkItems.patch({ visible: true });
      this._setState('LinkItems');
    } else {
      this._LinkItems.patch({ visible: false });
      this._setState('TrendingResults');
    }

    if (!trendingData.length) {
      this._TrendingResults.patch({ visible: false });
    } else {
      const items = trendingData.map(this.buildTrendingSearchTiles.bind(this));
      this._TrendingResultsList.patch({
        laneItems: items as LaneItem[],
      });
      this._TrendingResults.patch({ visible: true });
    }

    const recentData = AppData?.recentSearchesService.getRecentSearches();
    if (!recentData || !recentData.length) {
      this._RecentResults.patch({ visible: false });
    } else {
      const items = recentData.map(this.buildTrendingSearchTiles.bind(this));
      this._RecentResultsList.patch({
        laneItems: items as LaneItem[],
      });
      this._RecentResults.patch({ visible: true });
    }
  }

  private handleKeyCapture() {
    if (this._longPressKeyTime) {
      const isLongPress =
        Date.now() - this._longPressKeyTime > LONG_PRESS_THRESHOLD;
      if (isLongPress) this.setScrollTransition(true);
    } else {
      this.setScrollTransition(false);
      this._longPressKeyTime = Date.now();
    }
  }

  private setScrollTransition(isLongPress: boolean) {
    // Use standard scroll animation for long press to reduce jitter from bouncy animation
    const transition = isLongPress ? STANDARD_TRANSLATION.x : BOUNCY_SCROLL;
    this._LinkItems.patch({ scrollTransition: transition });
    this._TrendingResultsList.patch({ scrollTransition: transition });
    this._RecentResultsList.patch({ scrollTransition: transition });
  }

  $onTileSelected(data: Media, context?: SelectItemContext<TileParentContext>) {
    this.fireAncestors('$onTileSelected', data, context);
    if (!context || !context.parentContext) return;

    const seriesName = getSeriesName(data);
    const { mainIndex, crossIndex, rowTitle } = context.parentContext;

    const isTrending = this._TrendingResultsList.laneIndex === mainIndex;
    const isRecent = this._RecentResultsList.laneIndex === mainIndex;

    if (isTrending || isRecent) {
      reportItemSelected(
        {
          imageUrl: context?.imageUrl,
          mainIndex,
          crossIndex,
          data,
          rowTitle,
          isTivoTileSource: false,
        },
        PageId.SEARCH,
      );
    }

    reportBrowsePageEngagement(seriesName, rowTitle, mainIndex, crossIndex);
  }

  $expandDetailTile(shouldExpand: boolean) {
    if (shouldExpand) {
      if (this._TrendingResultsList.hasFocus()) {
        this.expandList(this._TrendingResults, this._TrendingResultsList, true);
        this.expandList(this._RecentResults, this._RecentResultsList, false);
      } else if (this._RecentResultsList.hasFocus()) {
        this.expandList(this._RecentResults, this._RecentResultsList, true);
        this.expandList(
          this._TrendingResults,
          this._TrendingResultsList,
          false,
        );
      }
    } else {
      const currentState = this._getState();
      if (
        currentState !== 'TrendingResults' &&
        currentState !== 'RecentResults'
      ) {
        this.collapseAllLists();
      }
    }
  }

  private expandList(
    list: Lightning.Element,
    listContainer: Lightning.Element,
    shouldExpand: boolean,
  ) {
    const targetHeight = shouldExpand
      ? EXPANDING_ROW_EXPANDED_HEIGHT
      : EXPANDING_ROW_COLLAPSED_HEIGHT;
    const listHeight = shouldExpand
      ? ExpandingDetailTile.expandedHeight
      : ExpandingDetailTile.height;

    // Delay is effectively a debounce due to compression and expansion competing with each other
    // Without a delay, components can bounce unexpectedly, often when holding key directions
    list.setSmooth('h', targetHeight, { ...BOUNCY_SCROLL, delay: 0.1 });
    listContainer.patch({ h: listHeight });
  }

  private collapseAllLists() {
    this.expandList(this._TrendingResults, this._TrendingResultsList, false);
    this.expandList(this._RecentResults, this._RecentResultsList, false);
  }

  private buildTrendingSearchTiles(mediaItem: Channel | Episode | Show) {
    return {
      ...mediaItem,
      customImageUrl: getSearchTileImage(mediaItem),
    };
  }

  private hasLinkItemsData() {
    return !!this._LinkItems.laneItems.length;
  }

  private hasTrendingData() {
    return !!this._TrendingResults.visible;
  }

  private hasRecentData() {
    return !!this._RecentResults.visible;
  }

  private generateLinkItems(exploreData: Swimlanes | null | undefined) {
    const linkItems = exploreData?.lanes?.[0]?.items;

    let label = '';
    let visible = false;
    let laneItems: ContentHubLaneItem[] = [];

    if (linkItems?.length) {
      label =
        exploreData?.lanes?.[0]?.title ?? translate('search.contentHubTitle');
      visible = true;
      laneItems = linkItems as ContentHubLaneItem[];
    }

    this._LinkItems.patch({
      label,
      laneItems,
      visible,
    });
  }

  static override _states() {
    return [
      class LinkItems extends this {
        override _getFocused() {
          return this._LinkItems;
        }

        override _handleDown() {
          if (this.hasTrendingData()) this._setState('TrendingResults');
          else if (this.hasRecentData()) this._setState('RecentResults');
        }
      },
      class TrendingResults extends this {
        override _getFocused() {
          return this._TrendingResultsList;
        }

        override _handleDown() {
          if (this.hasRecentData()) this._setState('RecentResults');
        }

        override _handleUp() {
          if (this.hasLinkItemsData()) {
            this._setState('LinkItems');
            return true;
          }

          return false;
        }

        override _handleBack() {
          // Backtracing logic. Set state to LinkItems in this component
          // so LinkItems gets focused when Down is pressed from SearchBar
          this.signal('$focusSearchBar');
          this._setState('LinkItems');
        }
      },

      class RecentResults extends this {
        override $enter() {
          this.signal('$shouldScrollDown', true);
        }

        override $exit() {
          this.signal('$shouldScrollDown', false);
        }

        override _getFocused() {
          return this._RecentResultsList;
        }

        override _handleUp() {
          this._handleBack();
        }

        override _handleBack() {
          if (this.hasTrendingData()) this._setState('TrendingResults');
          else this._setState('LinkItems');
        }
      },
    ];
  }
}
